import { fixedBottomShellClasses, useFixedBottomSpacer } from "@ui/hooks/use-fixed-bottom-spacer";
import { tv } from "@ui/lib/tv";
import type { ReactNode } from "react";

const FLOATING_SHADOW = "0 3px 10px 0 rgb(0 0 0 / 0.1), 0 1px 0 0 rgb(0 0 0 / 0.08)";

/** Container height, mirroring the `h-14` in `bottomBar`'s `container` slot. */
const FLOATING_HEIGHT = "3.5rem";
/** Bottom inset, mirroring the `pb-safe-[1rem]` in the `floating` root — see `pb-safe-*` in css/base.css. */
const FLOATING_INSET = "max(env(safe-area-inset-bottom), 1rem)";

/**
 * Vertical footprint of a `floating` bar. It is an overlay, not a shelf: it reserves no
 * in-flow space, so scroll containers running under it pad by this much to let their last
 * item clear it. Keep in step with the two class literals above.
 */
export const BOTTOM_BAR_FLOATING_OFFSET = `calc(${FLOATING_HEIGHT} + ${FLOATING_INSET})`;

const bottomBar = tv({
  defaultVariants: {
    variant: "docked",
    hasOverflow: false,
  },
  slots: {
    root: ["pointer-events-none", fixedBottomShellClasses, "flex justify-center md:hidden"],
    container: "pointer-events-auto flex h-14 w-full max-w-[28rem] items-center gap-1 bg-white px-2",
  },
  variants: {
    variant: {
      floating: {
        root: "px-4 pb-safe-[1rem]",
        container: "rounded-3xl",
      },
      docked: {
        root: "border-t border-neutral-100 bg-white pb-safe transition-shadow duration-300",
        container: "max-w-none",
      },
    },
    hasOverflow: {
      true: {},
      false: {},
    },
  },
  compoundVariants: [
    {
      variant: "docked",
      hasOverflow: true,
      class: { root: "border-t-neutral-200" },
    },
  ],
});

export interface BottomBarProps {
  children: ReactNode;
  variant?: "floating" | "docked";
  /** When true, darkens the docked variant's top border to indicate page content sits behind. */
  hasOverflow?: boolean;
  "aria-label": string;
  className?: string;
}

/**
 * Mobile global navigation bar — a floating or docked container of `BottomBarItem` links
 * pinned to the viewport bottom. Renders a `<nav>` (not a tablist) because each item is a
 * route navigation, and uses `aria-current="page"` on the active link. Hidden at `md+`.
 *
 * `docked` is a shelf: it reserves its height in flow with a spacer. `floating` is an
 * overlay: content runs beneath it and scroll containers reserve clearance with
 * `BOTTOM_BAR_FLOATING_OFFSET` padding instead (`Frame` does this for its scroller).
 */
export function BottomBar({
  children,
  variant = "docked",
  hasOverflow = false,
  "aria-label": ariaLabel,
  className,
}: BottomBarProps) {
  const {
    hasOverflow: showsOverflow,
    ref,
    spacer,
  } = useFixedBottomSpacer<HTMLElement>({ hasOverflow, spacerClassName: "md:hidden" });
  const styles = bottomBar({ variant, hasOverflow: showsOverflow });
  const containerStyle = variant === "floating" ? { boxShadow: FLOATING_SHADOW } : undefined;

  return (
    <>
      <nav ref={ref} aria-label={ariaLabel} className={styles.root({ className })} data-frame-chrome="bottombar">
        <div className={styles.container()} style={containerStyle}>
          {children}
        </div>
      </nav>
      {variant === "docked" ? spacer : null}
    </>
  );
}
